import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import type { Event, Ticket } from '@/lib/api';

/**
 * Cart Store
 * 
 * Manages shopping cart state locally.
 * Ticket locking happens at checkout via payment-intent endpoint.
 */

// ═══════════════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════════════

export interface CartItem {
  id: string; // unique cart item id
  ticketGroupId: number;
  eventId: number;
  event: {
    name: string;
    date: string;
    time: string;
    venue: string;
    location: string;
    imageUrl?: string;
  };
  ticket: {
    section: string;
    row: string;
    seats: string | null;
    priceCents: number;
    deliveryMethods: string[];
    isInstantDelivery: boolean;
  };
  quantity: number;
  addedAt: string;
}

export interface CartState {
  items: CartItem[];
  isLoading: boolean;
  error: string | null;
  
  addItem: (event: Event, ticket: Ticket, quantity: number) => void;
  removeItem: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  clearCart: () => void;
  clearError: () => void;
  getItem: (ticketGroupId: number) => CartItem | undefined;
}

// ═══════════════════════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════════════════════

function generateCartItemId(): string {
  return `cart_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

function eventToCartEvent(event: Event): CartItem['event'] {
  return {
    name: event.name,
    date: event.date,
    time: event.time,
    venue: event.venue.name,
    location: [event.venue.city, event.venue.state].filter(Boolean).join(', '),
    imageUrl: event.imageUrl,
  };
}

function ticketToCartTicket(ticket: Ticket): CartItem['ticket'] {
  return {
    section: ticket.section,
    row: ticket.row,
    seats: ticket.seats ? `${ticket.seats.low}-${ticket.seats.high}` : null,
    priceCents: Math.round(ticket.price.wholesale * 100),
    deliveryMethods: ticket.deliveryMethods,
    isInstantDelivery: ticket.isInstantDelivery,
  };
}

// ═══════════════════════════════════════════════════════════════════════════════════
// STORE
// ═══════════════════════════════════════════════════════════════════════════════════

export const useCartStore = create<CartState>()(
  persist(
    immer((set, get) => ({
      items: [],
      isLoading: false,
      error: null,
      
      addItem: (event, ticket, quantity) => {
        set((state) => {
          // Check if ticket group already in cart
          const existingIndex = state.items.findIndex(
            item => item.ticketGroupId === ticket.id
          );
          
          if (existingIndex >= 0) {
            // Update existing item quantity
            state.items[existingIndex].quantity = quantity;
            state.items[existingIndex].addedAt = new Date().toISOString();
          } else {
            // Add new item
            const item: CartItem = {
              id: generateCartItemId(),
              ticketGroupId: ticket.id,
              eventId: event.id,
              event: eventToCartEvent(event),
              ticket: ticketToCartTicket(ticket),
              quantity,
              addedAt: new Date().toISOString(),
            };
            state.items.push(item);
          }
          
          state.error = null;
        });
      },
      
      removeItem: (id) => {
        set((state) => {
          state.items = state.items.filter(item => item.id !== id);
        });
      },
      
      updateQuantity: (id, quantity) => {
        if (quantity < 1) {
          get().removeItem(id);
          return;
        }
        
        set((state) => {
          const item = state.items.find(i => i.id === id);
          if (item) {
            item.quantity = quantity;
          }
        });
      },
      
      clearCart: () => {
        set((state) => {
          state.items = [];
          state.error = null;
        });
      },
      
      clearError: () => set((state) => {
        state.error = null;
      }),
      
      getItem: (ticketGroupId) => {
        return get().items.find(item => item.ticketGroupId === ticketGroupId);
      },
    })),
    {
      name: 'bestix-cart',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        items: state.items,
      }),
    }
  )
);

// ═══════════════════════════════════════════════════════════════════════════════════
// SELECTORS
// ═══════════════════════════════════════════════════════════════════════════════════

export const selectCartItems = (state: CartState): CartItem[] => state.items;

export const selectCartItemCount = (state: CartState): number => 
  state.items.reduce((sum, item) => sum + item.quantity, 0);

export const selectCartSubtotalCents = (state: CartState): number =>
  state.items.reduce((sum, item) => sum + (item.ticket.priceCents * item.quantity), 0);

export const selectCartLoading = (state: CartState): boolean => state.isLoading;

export const selectCartError = (state: CartState): string | null => state.error;

export const selectIsCartEmpty = (state: CartState): boolean => state.items.length === 0;

export const selectCartItemByTicketGroupId = (ticketGroupId: number) => 
  (state: CartState): CartItem | undefined => 
    state.items.find(item => item.ticketGroupId === ticketGroupId);
